import React, { useCallback, useState } from "react";
import { Container, CustomDivider, SupplementInfoCard, Table } from "./styles";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { Creators as SupplementActions } from "../../../store/ducks/supplement";

// Libs
import { Col, Row, Spin, Tabs, Table as TableAntd } from "antd";

// Components
import EntityHistory from "../entityHistory";
import DrawerSupplementSustenanceCapacityDetails from "./supplementSustenanceCapacityDetails";

// Icons
import EyeIcon from "../../utils/icons/eye";
import NoteIcon from "../../utils/icons/note";

// Services
import { numberMask } from "../../../services/helpersMethodsService";
import { getSupplementShow } from "../../../services/supplementService";
import { getSupplierShow } from "../../../services/supplierService";

const NUTRIENT_UNITS = [
  { label: "g/kg", value: "g_kg" },
  { label: "mg/kg", value: "mg_kg" },
  { label: "%", value: "percentage" },
];

const SUPPLEMENT_GROUPS = {
  1: { id: 1, name: "Matérias-Primas" },
  2: { id: 2, name: "Suplementos & Concentrados" },
  3: { id: 3, name: "Rações" },
};

const SUPPLEMENT_SUB_GROUPS = {
  1: { id: 1, name: "Forragens Verdes", supplement_group_id: 1 },
  2: { id: 2, name: "Forragens Secas", supplement_group_id: 1 },
  3: { id: 3, name: "Resíduos Agrícolas", supplement_group_id: 1 },
  4: { id: 4, name: "Proteicos", supplement_group_id: 1 },
  5: { id: 5, name: "Energéticos", supplement_group_id: 1 },
  6: { id: 6, name: "Minerais", supplement_group_id: 1 },
  7: { id: 7, name: "S&C Prot/Energ", supplement_group_id: 2 },
  8: { id: 8, name: "S&C Prot/Energ/Minerais", supplement_group_id: 2 },
  9: { id: 9, name: "S Mineral", supplement_group_id: 2 },
  10: { id: 10, name: "S Ração", supplement_group_id: 3 },
};

const SUPPLEMENT_PRESENTATION = {
  1: { id: 1, name: "Gramínea" },
  2: { id: 2, name: "Silagem" },
  3: { id: 3, name: "Feno" },
  4: { id: 4, name: "Resíduo / Subproduto" },
  5: { id: 5, name: "Concentrado Seco" },
  6: { id: 6, name: "Concentrado Úmido" },
};

const RAW_MATERIAL_AMOUNT_UNIT = {
  percent_kg_ms: { label: "%/kgMS", value: "percent_kg_ms" },
  kg_ms_per_base_kg_ms: {
    label: "kg MS / X kg MS",
    value: "kg_ms_per_base_kg_ms",
  },
  kg_mo: { label: "kg MO", value: "kg_mo" },
};

const DrawerSupplementDetails = () => {
  /** Redux variables */
  const dispatch = useDispatch();

  const { drawerDetailsVisible, idSupplement } = useSelector(
    (state) => state.supplement,
  );

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
    user: { isInputSupplier },
  } = useSelector((state) => state.app);

  // State
  const [tabSelect, setTabSelect] = useState("1");
  const [supplementData, setSupplementData] = useState(null);
  const [isLoadingDrawer, setIsLoadingDrawer] = useState(false);
  const [showDrawerSustenanceCapacity, setShowDrawerSustenanceCapacity] =
    useState(false);

  /** Methods variables */
  const getUnit = useCallback((unitKey) => {
    return NUTRIENT_UNITS.find((unit) => unit.value === unitKey)?.label || "";
  }, []);
  function closeDrawer() {
    setTabSelect("1");
    setSupplementData(null);
    dispatch(SupplementActions.showHideSupplementDetails(null));
  }

  function closeSustenanceCapacityDrawer() {
    setShowDrawerSustenanceCapacity(false);
  }

  async function handleDrawerVisible(visible) {
    if (visible) {
      setIsLoadingDrawer(true);
      try {
        let {
          data: { results: response },
        } = await getSupplementShow({
          groupId,
          farmId,
          id: idSupplement,
          isInputSupplier,
        });

        let supplierName = null;

        if (response.supplierId && !isInputSupplier) {
          const {
            data: { results },
          } = await getSupplierShow({
            groupId: groupId,
            id: response.supplierId,
          });
          supplierName = results.name;
        }

        setSupplementData({ ...response, supplierName: supplierName });
      } catch (error) {
      } finally {
        setIsLoadingDrawer(false);
      }
    }
  }

  const handleTabClick = (key, e) => {
    setTabSelect(key);
  };

  function renderDetailsSupplementData() {
    return (
      <div className="drawerForm">
        {/* Group, Sub-Group, Apresentation */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.supplementGroupId}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {SUPPLEMENT_GROUPS[supplementData?.supplementGroupId].name}
              </span>
            </Row>
          </Col>
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>
                {translation.supplement.details.supplementSubGroupId}
              </label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {
                  SUPPLEMENT_SUB_GROUPS[supplementData?.supplementSubGroupId]
                    .name
                }
              </span>
            </Row>
          </Col>
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>
                {translation.supplement.details.supplementPresentationId}
              </label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {
                  SUPPLEMENT_PRESENTATION[
                    supplementData?.supplementPresentationId
                  ].name
                }
              </span>
            </Row>
          </Col>
        </Row>

        {/* Name */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <Row>
              <label>{translation.supplement.details.name}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">{supplementData.name}</span>
            </Row>
          </Col>
        </Row>

        {/* Industrial Name */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <Row>
              <label>{translation.supplement.details.industrialName}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {supplementData.industrialName}
              </span>
            </Row>
          </Col>
        </Row>

        {/* Supplier */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <Row>
              <label>{translation.supplement.details.supplierName}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {supplementData.supplierName}
              </span>
            </Row>
          </Col>
        </Row>

        {/* Cost and Proportion */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={12} sm={12} md={12} lg={12} xl={12}>
            <Row>
              <label>{translation.supplement.details.cost}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.cost, true, null, null, 5, 5)} Kg
              </span>
            </Row>
          </Col>
          <Col xs={12} sm={12} md={12} lg={12} xl={12}>
            <Row>
              <label>{translation.supplement.details.ratio}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.proportion, false)} %
              </span>
            </Row>
          </Col>
        </Row>

        {supplementData.supplementFormulated && (
          <Row type="flex" justify="start" gutter={8}>
            {/* Select Raw Material */}
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <CustomDivider dashed>Matéria(s) Prima(s)</CustomDivider>
              {/* List Materials */}
              <Row type="flex" justify="start">
                <Col span={24}>
                  <TableAntd
                    dataSource={supplementData.formulateItems || []}
                    rowKey={"id"}
                    pagination={false}
                  >
                    <TableAntd.Column
                      title="Mat. Prima"
                      dataIndex="supplementOrRawMaterialName"
                    />
                    <TableAntd.Column
                      title="Qtd"
                      render={(text, record) => {
                        if (record.amountUnit === "kg_mo") {
                          return numberMask(record.originalMatterAmount);
                        }
                        if (record.amountUnit === "kg_ms_per_base_kg_ms") {
                          return `${numberMask(
                            record.addedDryMatterAmount,
                          )} / ${numberMask(record.baseDryMatterAmount)}`;
                        }
                        return record.amount;
                      }}
                    />
                    <TableAntd.Column
                      title="Unidade"
                      dataIndex="amountUnit"
                      render={(text, record) =>
                        RAW_MATERIAL_AMOUNT_UNIT[text]?.label
                      }
                    />
                  </TableAntd>
                </Col>
              </Row>
            </Col>
          </Row>
        )}

        {supplementData.observations && (
          <Row type="flex" justify="start">
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <Row>
                <label>{translation.supplement.form.observations}</label>
              </Row>
              <Row className="rowInput">
                <span
                  className="rowInputData"
                  style={{ whiteSpace: "pre-line" }}
                >
                  {supplementData.observations}
                </span>
              </Row>
            </Col>
          </Row>
        )}

        <CustomDivider dashed>
          {translation.supplement.details.nutritionalDataDivider}
        </CustomDivider>

        {/* Tnd and Cp */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={12} sm={12} md={12} lg={12} xl={12}>
            <Row>
              <label>{translation.supplement.details.tdn}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.tdn, false)}{" "}
                {getUnit(supplementData.tdnUnit)}
              </span>
            </Row>
          </Col>
          <Col xs={12} sm={12} md={12} lg={12} xl={12}>
            <Row>
              <label>{translation.supplement.details.cp}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.cp)} {getUnit(supplementData.cpUnit)}
              </span>
            </Row>
          </Col>
        </Row>

        {/* NPN and Dry Matter Percentage */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={12} sm={12} md={12} lg={12} xl={12}>
            <Row>
              <label>{translation.supplement.details.npn}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.npn, false)} g/Kg
              </span>
            </Row>
          </Col>
          <Col xs={12} sm={12} md={12} lg={12} xl={12}>
            <Row>
              <label>
                {translation.supplement.details.dryMatterPercentage}
              </label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.dryMatterPercentage)} %MS
              </span>
            </Row>
          </Col>
        </Row>

        {/* Calcium, Phosphorus and Nitrogen */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.calcium}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.calcium)}{" "}
                {getUnit(supplementData.calciumUnit)}
              </span>
            </Row>
          </Col>
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.phosphorus}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.phosphorus)}{" "}
                {getUnit(supplementData.phosphorusUnit)}
              </span>
            </Row>
          </Col>
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.nitrogen}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.nitrogen)}{" "}
                {getUnit(supplementData.nitrogenUnit)}
              </span>
            </Row>
          </Col>
        </Row>
        {/* Potassium, Sulfur */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.potassium}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.potassium)}{" "}
                {getUnit(supplementData.potassiumUnit)}
              </span>
            </Row>
          </Col>
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.sulfur}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.sulfur)}{" "}
                {getUnit(supplementData.sulfurUnit)}
              </span>
            </Row>
          </Col>
        </Row>

        {/* Zinc, Sodium and Additive Effect */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.zinc}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.zinc)}{" "}
                {getUnit(supplementData.zincUnit)}
              </span>
            </Row>
          </Col>
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.sodium}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.sodium)}{" "}
                {getUnit(supplementData.sodiumUnit)}
              </span>
            </Row>
          </Col>
        </Row>

        {/* FDA, Lignin and Ee */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.fda}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.fda)}
                {getUnit(supplementData.fdaUnit)}
              </span>
            </Row>
          </Col>

          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.lignin}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.lignin)} g/Kg
              </span>
            </Row>
          </Col>
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.ee}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.ee)} %/MS
              </span>
            </Row>
          </Col>
        </Row>

        {/* FDN, PDR, PNDR */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.fdn}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.fdn)} %
              </span>
            </Row>
          </Col>

          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.pdr}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.pdr)} %PB
              </span>
            </Row>
          </Col>
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.pndr}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.pndr)} %PB
              </span>
            </Row>
          </Col>
        </Row>

        {/* EM, FEF, FDNfe */}
        <Row type="flex" style={{ marginTop: "13px" }} align="middle">
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.em}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.em)} Mcal/kg
              </span>
            </Row>
          </Col>

          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.fef}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.fef)} %
              </span>
            </Row>
          </Col>
          <Col xs={8} sm={8} md={8} lg={8} xl={8}>
            <Row>
              <label>{translation.supplement.details.fdnfe}</label>
            </Row>
            <Row className="rowInput">
              <span className="rowInputData">
                {numberMask(supplementData.fdnfe)} %
              </span>
            </Row>
          </Col>
        </Row>

        <CustomDivider dashed>
          {translation.supplement.details.capacityDivider}
        </CustomDivider>

        <Row style={{ marginTop: "13px" }}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <Table>
              <tbody>
                <tr>
                  <td>
                    <span>
                      {translation.supplement.details.sustenanceCapacity}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      style={{ marginLeft: "60px" }}
                      onClick={() => setShowDrawerSustenanceCapacity(true)}
                    >
                      <EyeIcon isActive />
                    </button>
                  </td>
                </tr>
              </tbody>
            </Table>
          </Col>
        </Row>

        <SupplementInfoCard>
          <NoteIcon /> {translation.supplement.details.information}
        </SupplementInfoCard>
      </div>
    );
  }

  return (
    <Container
      title={"Detalhes do Suplemento"}
      width={600}
      afterVisibleChange={handleDrawerVisible}
      onClose={closeDrawer}
      maskClosable={false}
      visible={drawerDetailsVisible}
    >
      <Spin spinning={isLoadingDrawer}>
        <Tabs
          type="card"
          defaultActiveKey="1"
          activeKey={tabSelect}
          onTabClick={handleTabClick}
          forceRender={true}
        >
          <Tabs.TabPane
            tab={
              <span>{translation.supplement.details.tabs.supplementData}</span>
            }
            key="1"
          >
            {supplementData != null && renderDetailsSupplementData()}
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                {translation.supplement.details.tabs.supplementHistory}
              </span>
            }
            key="2"
          >
            {idSupplement !== null && (
              <EntityHistory entity="Supplement" entityId={idSupplement} />
            )}
          </Tabs.TabPane>
        </Tabs>
      </Spin>
      <DrawerSupplementSustenanceCapacityDetails
        drawerVisible={showDrawerSustenanceCapacity}
        data={supplementData}
        onCancel={closeSustenanceCapacityDrawer}
      />
    </Container>
  );
};

export default DrawerSupplementDetails;
